import { useMemo } from 'react'
import {
  CUSTOM_TABLE_TYPES,
  DAY,
  HOUR,
  isNotEmpty,
  messageFromError,
  sortCustomFields,
  type CustomFieldData,
  type CustomFieldId,
  type CustomFieldListInput,
  type CustomFieldListSuccess,
  type CustomTableType,
  type WorkflowId,
} from '@terros/common'
import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { type GetHookResponse } from '../hooks'
import { useServerRequest } from '../api'
import { customFieldsKey } from './keys'

export function useCustomFields(
  tableType: CustomTableType,
  input?: Omit<CustomFieldListInput, 'tableType'>
): GetHookResponse<CustomFieldData[]> {
  return useTantalimQuery<CustomFieldListInput, CustomFieldListSuccess, CustomFieldData[]>({
    url: '/customField/list',
    queryKey: customFieldsKey(tableType, input),
    input: {
      tableType,
      ...input,
    },
    convertResponse: (data) => sortCustomFields(data.customFields),
    options: {
      staleTime: HOUR,
      gcTime: DAY,
    },
  })
}

export function useAccountCustomFields(
  input?: Omit<CustomFieldListInput, 'tableType'>
): GetHookResponse<CustomFieldData[]> {
  return useCustomFields('Account', input)
}

/**
 * Archived custom fields across every table type as one flat list, for the trash page.
 * Custom fields are listed per table type, so the trash view has to fan out and merge.
 */
export function useArchivedCustomFields(): GetHookResponse<CustomFieldData[]> {
  const serverRequest = useServerRequest<CustomFieldListInput, CustomFieldListSuccess>('/customField/list')

  return useQueries({
    queries: CUSTOM_TABLE_TYPES.map((tableType) => ({
      queryKey: customFieldsKey(tableType, { showArchived: true }),
      queryFn: async (): Promise<CustomFieldData[]> => {
        const response = await serverRequest({ tableType, showArchived: true })
        return response.customFields
      },
      retry: false,
      staleTime: HOUR,
      gcTime: DAY,
    })),
    combine: combineArchivedCustomFields,
  })
}

function combineArchivedCustomFields(
  results: UseQueryResult<CustomFieldData[], Error>[]
): GetHookResponse<CustomFieldData[]> {
  const updatedAt = results.map((result) => result.dataUpdatedAt).filter(isNotEmpty)

  return {
    data: sortArchivedCustomFields(results.flatMap((result) => result.data ?? [])),
    error: results.map((result) => messageFromError(result.error)).find(isNotEmpty),
    isFetching: results.some((result) => result.isFetching),
    isLoading: results.some((result) => result.isLoading),
    isPlaceholderData: results.some((result) => result.isPlaceholderData),
    isSuccess: results.every((result) => result.isSuccess),
    lastUpdatedAt: isNotEmpty(updatedAt) ? Math.max(...updatedAt) : undefined,
    refetch: () => {
      results.forEach((result) => {
        result.refetch()
      })
    },
  }
}

/**
 * Groups by table type, then by label. The per-type `order` that `sortCustomFields` keys on is
 * meaningless once the types are merged — it would interleave entities into an arbitrary jumble.
 */
function sortArchivedCustomFields(customFields: CustomFieldData[]): CustomFieldData[] {
  return customFields.sort((a, b) => {
    if (a.tableType === b.tableType) return a.label.localeCompare(b.label)
    return CUSTOM_TABLE_TYPES.indexOf(a.tableType) - CUSTOM_TABLE_TYPES.indexOf(b.tableType)
  })
}

export const useCustomFieldsMap = (workflowId?: WorkflowId): Map<CustomFieldId, CustomFieldData> => {
  const { data: customFields } = useAccountCustomFields(workflowId ? { workflowId } : undefined)
  return useMemo(() => {
    return new Map((customFields ?? []).map((customField) => [customField.fieldId, customField]))
  }, [customFields])
}
